// Simple "select a record to delete" dropdown for the record-based sections
// (risk location / coverage / schedule / interested party) when the board's
// "Delete" action is chosen. A single dropdown, no checkboxes.

import React from "react";
import { Grid } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../../components/formsComponents";
import { IHabitationalPolicyChangeProps } from "../../Steps/HabitationalPolicyChange.types";
import { useHabitationalPolicyRiskLocation } from "../../../hooks/useHabitationalPolicyRiskLocation";
import { useHabitationalCoevrage } from "../../../hooks/useHabitationalCoverage";
import { useHabitationalSchedule } from "../../../hooks/useHabitationalSchedule";
import { useHabitationalIntrestedParty } from "../../../hooks/useHabitationalIntrestedParty";
import { RecordSectionKey } from "./RecordPanelHost";

interface IDeletePickerV2Props extends IHabitationalPolicyChangeProps {
  section: RecordSectionKey;
}

const CONFIG: Record<
  IDeletePickerV2Props["section"],
  { label: string; field: string }
> = {
  riskLocationChange: {
    label: "Select Risk Location to Delete",
    field: "risk_location_change.delete_risk_location",
  },
  coverageChange: {
    label: "Select Coverage to Delete",
    field: "coverage_change.delete_coverage",
  },
  scheduleChange: {
    label: "Select Schedule to Delete",
    field: "schedule_change.delete_schedule",
  },
  interestedPartyChange: {
    label: "Select Interested Party to Delete",
    field: "interested_party_change.delete_interested_party",
  },
};

export const DeletePickerV2: React.FC<IDeletePickerV2Props> = ({
  section,
  values,
  handleChange,
  customerPolicyId,
}) => {
  const { riskLocationsDropDown } =
    useHabitationalPolicyRiskLocation(customerPolicyId);
  const { coverageDropDown } = useHabitationalCoevrage(customerPolicyId);
  const { scheduleDropdown } = useHabitationalSchedule(customerPolicyId);
  const { interestedPartyDropDown } =
    useHabitationalIntrestedParty(customerPolicyId);

  const options =
    section === "riskLocationChange"
      ? riskLocationsDropDown
      : section === "coverageChange"
      ? coverageDropDown
      : section === "scheduleChange"
      ? scheduleDropdown
      : interestedPartyDropDown;

  const { label, field } = CONFIG[section];

  const currentValue =
    section === "riskLocationChange"
      ? values.risk_location_change.delete_risk_location
      : section === "coverageChange"
      ? values.coverage_change.delete_coverage
      : section === "scheduleChange"
      ? values.schedule_change.delete_schedule
      : values.interested_party_change.delete_interested_party;

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        <CustomFormLabel>{label}</CustomFormLabel>
        <ControlledCustomSelect
          variant="outlined"
          size="small"
          type="string"
          name={field}
          fullWidth
          displayEmpty
          value={currentValue ?? ""}
          onChange={handleChange}
          placeholder="Select One"
          options={options as any}
          // Rendered inside the V2 right-side drawer (zIndex 1400); the menu
          // portals to the body at the default modal zIndex (1300) and would
          // open behind the drawer. Keep it above the drawer.
          MenuProps={{ sx: { zIndex: 1600 } }}
        />
      </Grid>
    </Grid>
  );
};
